// 1. Imports React
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

// 2. Imports externes
import {
  ArrowLeft,
  Building2,
  CircleCheckBig,
  CircleSlash2,
  Clock,
  Edit,
  ToggleLeft,
  ToggleRight,
  Users,
} from "lucide-react";

// 3. Imports composants UI
import { CompanyFormDialog } from "@/components/forms/CompanyFormDialog";
import { CompanyInfos } from "@/components/CompanyInfos";
import { CompanyIdentifiers } from "@/components/CompanyIdentifiers";
import { CompanyUsers } from "@/components/CompanyUsers";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { showErrorToast, showSuccessToast } from "@/components/ui/toast";

// 4. Imports hooks personnalisés
import { useCompany, useUpdateCompany } from "@/hooks/useCompanies";

// 5. Composant
export const CompanyDetailsPage = () => {
  // ========== A. Hooks React ==========
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);

  // ========== B. Custom Hooks ==========
  const {
    data: companyData,
    isLoading: loading,
    error: queryError,
  } = useCompany(id!);
  const company = companyData?.company || null;
  const companyStats = companyData?.stats || null;

  const { mutate: updateCompanyMutation } = useUpdateCompany();

  // ========== C. Fonctions utilitaires ==========
  // Formater une durée en heures et minutes
  const formatDuration = (hours: number): string => {
    if (!hours || hours === 0) return "-";

    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    if (h > 0) {
      return `${h}h ${m}m`;
    } else {
      return `${m}m`;
    }
  };

  // ========== D. Handlers ==========
  // Basculer le statut actif/inactif de l'entreprise
  const handleToggleCompanyStatus = () => {
    if (!company) return;
    updateCompanyMutation(
      { id: company.id, data: { isActive: !company.isActive } },
      {
        onSuccess: () => {
          showSuccessToast(
            company.isActive
              ? "Entreprise désactivée avec succès"
              : "Entreprise activée avec succès"
          );
        },
        onError: (error: Error) => {
          showErrorToast(
            error.message ||
              "Erreur lors de la modification du statut de l'entreprise"
          );
        },
      }
    );
  };

  // Fermer le modal après modification réussie
  const handleModalSuccess = () => {
    setDialogOpen(false);
  };

  // ========== E. Gestion des erreurs et états de chargement ==========
  // Afficher le skeleton pendant le chargement
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="outline"
              size="sm"
              disabled
              onClick={() => navigate("/superuser/companies")}
              className="gap-2 shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Retour</span>
            </Button>
            <div>
              <Skeleton className="h-8 w-48 sm:w-64" />
              <Skeleton className="h-4 w-32 mt-2" />
            </div>
          </div>
        </div>
        <div className="grid gap-4 sm:gap-6 grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="p-4 sm:p-6">
              <div className="space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-8 w-1/2" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Afficher l'erreur si la requête a échoué
  if (queryError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/superuser/companies")}
            className="gap-2 shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Retour</span>
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold">
            Erreur
          </h1>
        </div>
        <Card className="p-4 sm:p-6">
          <div className="text-center">
            <p className="text-red-600 mb-2">Erreur</p>
            <p className="text-sm sm:text-base text-gray-600">
              {queryError?.message}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Afficher un message si l'entreprise n'est pas trouvée
  if (!company) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/superuser/companies")}
            className="gap-2 shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Retour</span>
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold">
            Entreprise non trouvée
          </h1>
        </div>
      </div>
    );
  }

  // ========== F. JSX ==========
  return (
    <div className="space-y-6">
      {/* En-tête avec actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/superuser/companies")}
            className="gap-2 shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Retour</span>
          </Button>
          <div className="lg:ml-0 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold truncate flex items-center gap-3">
              <Building2 className="h-8 w-8 text-primary shrink-0" />
              <span className="truncate">{company?.name}</span>
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Détails de l'entreprise
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={handleToggleCompanyStatus}
            className="gap-2 w-full sm:w-auto"
            size="sm"
          >
            {company?.isActive ? (
              <>
                <ToggleLeft className="h-4 w-4" />
                Désactiver
              </>
            ) : (
              <>
                <ToggleRight className="h-4 w-4" />
                Activer
              </>
            )}
          </Button>
          <Button
            className="gap-2 w-full sm:w-auto"
            onClick={() => setDialogOpen(true)}
            size="sm"
          >
            <Edit className="h-4 w-4" />
            Modifier
          </Button>
        </div>
      </div>

      {/* Cartes de statistiques */}
      <div className="grid gap-4 sm:gap-6 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Utilisateurs</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {companyStats?.activeEmployees}
            </div>
            <p className="text-xs text-muted-foreground">
              {companyStats?.activeEmployees} actifs
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sessions</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {companyStats?.sessionsThisMonth}
            </div>
            <p className="text-xs text-muted-foreground">Ce mois-ci</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Heures totales
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatDuration(companyStats?.totalHoursThisMonth || 0)}
            </div>
            <p className="text-xs text-muted-foreground">Ce mois-ci</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Statut</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Badge
              variant={company?.isActive ? "default" : "secondary"}
              className={
                company?.isActive
                  ? "bg-success hover:bg-success"
                  : "bg-error hover:bg-error text-error-foreground"
              }
            >
              {company?.isActive ? (
                <CircleCheckBig className="h-3 w-3" />
              ) : (
                <CircleSlash2 className="h-3 w-3" />
              )}{" "}
              {company?.isActive ? "Actif" : "Inactif"}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Onglets Infos, Identifiant et Utilisateurs */}
      <Card className="p-4 sm:p-6">
        <Tabs defaultValue="infos" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4 sm:mb-6 bg-white p-0 shadow-none h-auto">
            <TabsTrigger
              value="infos"
              className="data-[state=active]:border-b-2 data-[state=active]:border-b-primary data-[state=active]:text-primary rounded-none text-xs sm:text-sm py-2 sm:py-3"
            >
              Infos
            </TabsTrigger>
            <TabsTrigger
              value="identifiant"
              className="data-[state=active]:border-b-2 data-[state=active]:border-b-primary data-[state=active]:text-primary rounded-none text-xs sm:text-sm py-2 sm:py-3"
            >
              Identifiant
            </TabsTrigger>
            <TabsTrigger
              value="utilisateurs"
              className="data-[state=active]:border-b-2 data-[state=active]:border-b-primary data-[state=active]:text-primary rounded-none text-xs sm:text-sm py-2 sm:py-3"
            >
              Utilisateurs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="infos" className="space-y-4 sm:space-y-6 mt-0">
            <CompanyInfos company={company} />
          </TabsContent>
          <TabsContent
            value="identifiant"
            className="space-y-4 sm:space-y-6 mt-0"
          >
            <CompanyIdentifiers company={company} />
          </TabsContent>
          <TabsContent
            value="utilisateurs"
            forceMount
            className="data-[state=inactive]:hidden mt-0"
          >
            <CompanyUsers companyId={id!} />
          </TabsContent>
        </Tabs>
      </Card>

      {/* Dialog de modification */}
      <CompanyFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        company={company}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};
